import { api } from './api';

export type AllocationValidationRunStatus = 'Completed' | 'In Progress';
export type AllocationValidationRunStatusFilter = 'all' | 'completed' | 'in_progress';

export interface AllocationValidationRun {
  id: string;
  name: string;
  description: string;
  period: string;
  totalRules: number;
  runType: string;
  createdAt: string;
  completedAt: string | null;
  createdBy: string;
  status: AllocationValidationRunStatus;
}

export interface AllocationValidationRunsResult {
  runs: AllocationValidationRun[];
  total: number;
  limit?: number | null;
  offset?: number;
  hasMore?: boolean;
  nextOffset?: number | null;
}

export interface AllocationValidationRunsQuery {
  search?: string;
  status?: AllocationValidationRunStatusFilter;
  limit?: number;
  offset?: number;
}

export interface AllocationDataSourceSummary {
  datasourceId: string;
  name: string;
  type: string;
  isActive: boolean;
}

export interface AllocationSapConnectionStatus {
  connected: boolean;
  datasource: AllocationDataSourceSummary | null;
}

export interface AllocationValidationRule {
  id: string;
  name: string;
  allocKpi: string;
  allocStep: string;
  ratioKpi: string;
  rule: string;
  source: string;
}

export interface AllocationValidationRulesQuery {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AllocationValidationRulesResult {
  rules: AllocationValidationRule[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset: number | null;
}

export interface AllocationValidationCreateRunInput {
  title: string;
  description: string;
  period: string;
  ruleIds: string[];
}

export interface AllocationValidationCreateRunPayload {
  name: string;
  description: string;
  period: string;
  rule_ids: Array<number | string>;
}

export interface AllocationValidationRunDetailRule {
  ruleId: string;
  label: string;
  rule: string;
  source: string;
  allocStep: string;
  ratioKpi: string;
  allocKpi: string;
  totalRows: string;
  numberOfKeys: string;
  sourceKeys: string;
  targetKeys: string;
  matchedKeys: string;
  sourceOnlyKeys: string;
  targetOnlyKeys: string;
  passed: string;
  failed: string;
  audit: string;
  exceptions: string;
  validationResult: string;
  startedAt: string;
  completedAt: string;
  status: string;
  errorMessage: string;
  blobUrl: string;
}

export interface AllocationValidationRunDetail {
  runId: string;
  name: string;
  description: string;
  period: string;
  rules: AllocationValidationRunDetailRule[];
}

type JsonRecord = Record<string, unknown>;

const BASE = '/api/v1/allocation';

const isRecord = (value: unknown): value is JsonRecord => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const readString = (source: JsonRecord, keys: string[], fallback = ''): string => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return fallback;
};

const readNullableString = (source: JsonRecord, keys: string[]): string | null => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return null;
};

const readNumber = (source: JsonRecord, keys: string[]): number | null => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
};

const readBoolean = (source: JsonRecord, keys: string[]): boolean | null => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }
  }
  return null;
};

const normalizeStatus = (status: unknown): AllocationValidationRunStatus => {
  if (typeof status !== 'string') return 'In Progress';

  const normalized = status.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['completed', 'complete', 'success', 'succeeded', 'failed', 'failure', 'error'].includes(normalized)) {
    return 'Completed';
  }
  return 'In Progress';
};

const extractRunsArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  const candidates = [payload.runs, payload.items, payload.data, payload.results];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    const nested = extractRunsArray(candidate);
    if (nested.length > 0) return nested;
  }

  return [];
};

const extractRulesArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  const candidates = [payload.rules, payload.items, payload.data, payload.results];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    const nested = extractRulesArray(candidate);
    if (nested.length > 0) return nested;
  }

  return [];
};

const extractTotal = (payload: unknown, fallback: number): number => {
  if (!isRecord(payload)) return fallback;

  const directTotal = readNumber(payload, ['total', 'total_count', 'totalCount', 'count']);
  if (directTotal !== null) return directTotal;

  for (const key of ['data', 'metadata', 'meta', 'pagination']) {
    const nested = payload[key];
    if (!isRecord(nested)) continue;
    const nestedTotal = readNumber(nested, ['total', 'total_count', 'totalCount', 'count']);
    if (nestedTotal !== null) return nestedTotal;
  }

  return fallback;
};

const unwrapData = (payload: unknown): unknown => {
  if (!isRecord(payload)) return payload;
  return payload.data ?? payload;
};

const mapRun = (entry: unknown, index: number): AllocationValidationRun => {
  const source = isRecord(entry) ? entry : {};

  return {
    id: readString(source, ['id', 'run_id', 'runId', 'uuid'], `allocation-run-${index + 1}`),
    name: readString(source, ['title', 'name', 'run_name', 'runName'], 'Untitled run'),
    description: readString(source, ['description', 'details', 'summary']),
    period: readString(source, ['period']),
    totalRules: readNumber(source, ['total_rules', 'totalRules', 'rules_count', 'rulesCount']) ?? 0,
    runType: readString(source, ['run_type', 'runType', 'type']),
    createdBy: readString(source, [
      'created_by_name',
      'createdByName',
      'created_by_display_name',
      'createdByDisplayName',
      'created_by',
      'createdBy',
      'owner',
      'created_by_user',
      'createdByUser',
    ]),
    createdAt: readString(source, [
      'created_at',
      'createdAt',
      'created_date_time',
      'createdDateTime',
      'created_datetime',
      'createdDatetime',
      'started_at',
      'startedAt',
    ]),
    completedAt: readNullableString(source, [
      'completed_at',
      'completedAt',
      'completed_date_time',
      'completedDateTime',
      'completed_datetime',
      'completedDatetime',
      'finished_at',
      'finishedAt',
      'ended_at',
      'endedAt',
    ]),
    status: normalizeStatus(source.status ?? source.run_status ?? source.runStatus ?? source.state),
  };
};

const mapRunDetailRule = (entry: unknown, index: number): AllocationValidationRunDetailRule => {
  const source = isRecord(entry) ? entry : {};
  const ruleId = readString(source, ['rule_id', 'ruleId', 'id'], `rule-${index + 1}`);
  const ruleText = readString(source, ['rule', 'label', 'name'], `Rule ${ruleId}`);

  return {
    ruleId,
    label: readString(source, ['label', 'name', 'rule'], ruleText),
    rule: ruleText,
    source: readString(source, ['source']),
    allocStep: readString(source, ['alloc_step', 'allocStep']),
    ratioKpi: readString(source, ['ratio_kpi', 'ratioKpi']),
    allocKpi: readString(source, ['alloc_kpi', 'allocKpi']),
    totalRows: readString(source, ['total_rows', 'totalRows']),
    numberOfKeys: readString(source, ['number_of_keys', 'numberOfKeys']),
    sourceKeys: readString(source, ['source_keys', 'sourceKeys']),
    targetKeys: readString(source, ['target_keys', 'targetKeys']),
    matchedKeys: readString(source, ['matched_keys', 'matchedKeys']),
    sourceOnlyKeys: readString(source, ['source_only_keys', 'sourceOnlyKeys']),
    targetOnlyKeys: readString(source, ['target_only_keys', 'targetOnlyKeys']),
    passed: readString(source, ['passed', 'pass']),
    failed: readString(source, ['failed']),
    audit: readString(source, ['audit']),
    exceptions: readString(source, ['exceptions', 'failed']),
    validationResult: readString(source, ['validation_result', 'validationResult']),
    startedAt: readString(source, ['started_at', 'startedAt']),
    completedAt: readString(source, ['completed_at', 'completedAt']),
    status: readString(source, ['status']),
    errorMessage: readString(source, ['error_message', 'errorMessage']),
    blobUrl: readString(source, ['blob_url', 'blobUrl']),
  };
};

const mapRunDetail = (payload: unknown): AllocationValidationRunDetail => {
  const source = isRecord(payload) ? payload : {};
  const rawRules = Array.isArray(source.rules) ? source.rules : [];

  return {
    runId: readString(source, ['run_id', 'runId', 'id']),
    name: readString(source, ['name', 'title']),
    description: readString(source, ['description']),
    period: readString(source, ['period']),
    rules: rawRules.map(mapRunDetailRule),
  };
};

const mapRule = (entry: unknown, index: number): AllocationValidationRule => {
  const source = isRecord(entry) ? entry : {};
  const ruleText = readString(source, ['rule', 'name', 'rule_name', 'ruleName', 'title'], `Rule ${index + 1}`);

  return {
    id: readString(source, ['id', 'rule_id', 'ruleId', 'uuid'], `allocation-rule-${index + 1}`),
    name: ruleText,
    allocKpi: readString(source, ['alloc_kpi', 'allocKpi']),
    allocStep: readString(source, ['alloc_step', 'allocStep']),
    ratioKpi: readString(source, ['ratio_kpi', 'ratioKpi']),
    rule: ruleText,
    source: readString(source, ['source']),
  };
};

const matchesRuleSearch = (rule: AllocationValidationRule, search?: string): boolean => {
  const normalizedSearch = search?.trim().toLowerCase();
  if (!normalizedSearch) return true;
  const normalizedAliasSearch = normalizedSearch.replace(/[^a-z0-9]/g, '');
  const sourceDisplay = rule.source.trim() || (
    rule.allocStep.trim().toLowerCase() === 'ratio' ? 'Shared' : ''
  );
  const matchesSearchValue = (value: string) => {
    const normalizedValue = value.toLowerCase();
    if (normalizedValue.includes(normalizedSearch)) return true;
    return Boolean(normalizedAliasSearch) && normalizedValue.replace(/[^a-z0-9]/g, '').includes(normalizedAliasSearch);
  };

  return [rule.name, rule.rule, sourceDisplay, rule.source, rule.allocStep, rule.ratioKpi, rule.allocKpi].some(matchesSearchValue);
};

const matchesRunSearch = (run: AllocationValidationRun, search?: string): boolean => {
  const normalizedSearch = search?.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return [run.name, run.description, run.period, run.createdBy, run.status].some((value) => (
    value.toLowerCase().includes(normalizedSearch)
  ));
};

const matchesRunStatusFilter = (
  run: AllocationValidationRun,
  statusFilter?: AllocationValidationRunStatusFilter,
): boolean => {
  if (!statusFilter || statusFilter === 'all') return true;
  if (statusFilter === 'completed') return run.status === 'Completed';
  return run.status === 'In Progress';
};

const mapRuleIdForPayload = (ruleId: string): number | string => {
  const trimmed = ruleId.trim();
  if (/^\d+$/.test(trimmed)) {
    const numericId = Number(trimmed);
    if (Number.isSafeInteger(numericId)) return numericId;
  }
  return ruleId;
};

const buildRunsQueryParams = (query: AllocationValidationRunsQuery = {}) => {
  const params: Record<string, string | number> = {};
  const search = query.search?.trim();

  if (search) params.search = search;
  if (query.status && query.status !== 'all') {
    params.status = query.status === 'in_progress' ? 'pending,running' : 'completed';
  }
  if (typeof query.limit === 'number') params.limit = query.limit;
  if (typeof query.offset === 'number') params.offset = query.offset;

  return params;
};

const buildRulesQueryParams = (query: AllocationValidationRulesQuery = {}) => {
  const params: Record<string, string | number> = {};
  const search = query.search?.trim();

  if (search) params.search = search;
  if (typeof query.limit === 'number') params.limit = query.limit;
  if (typeof query.offset === 'number') params.offset = query.offset;

  return params;
};

const mapRulesResult = (
  payload: unknown,
  query: AllocationValidationRulesQuery = {},
): AllocationValidationRulesResult => {
  const source = isRecord(payload) ? payload : {};
  const rawRules = extractRulesArray(payload).map(mapRule);
  const serverLimit = readNumber(source, ['limit', 'page_size', 'pageSize']);
  const serverOffset = readNumber(source, ['offset']);
  const serverNextOffset = readNumber(source, ['next_offset', 'nextOffset']);
  const serverHasMore = readBoolean(source, ['has_more', 'hasMore']);
  const hasServerPagination = (
    serverLimit !== null ||
    serverOffset !== null ||
    serverNextOffset !== null ||
    serverHasMore !== null
  );
  const limit = serverLimit ?? query.limit ?? rawRules.length;
  const offset = readNumber(source, ['offset']) ?? query.offset ?? 0;
  const clientFilteredRules = hasServerPagination
    ? rawRules
    : rawRules.filter((rule) => matchesRuleSearch(rule, query.search));
  const rules = hasServerPagination
    ? clientFilteredRules
    : clientFilteredRules.slice(offset, offset + limit);
  const total = hasServerPagination
    ? extractTotal(payload, rawRules.length)
    : clientFilteredRules.length;
  const hasMore = serverHasMore
    ?? (serverNextOffset !== null ? true : offset + rules.length < total);

  return {
    rules,
    total,
    limit,
    offset,
    hasMore,
    nextOffset: hasMore ? (serverNextOffset ?? offset + limit) : null,
  };
};

const fetchRulesPage = async (
  query: AllocationValidationRulesQuery = {},
): Promise<AllocationValidationRulesResult> => {
  const { data } = await api.get<unknown>(`${BASE}/rules`, {
    params: buildRulesQueryParams(query),
  });

  return mapRulesResult(data, query);
};

const mapCreateRunPayload = (
  input: AllocationValidationCreateRunInput,
): AllocationValidationCreateRunPayload => ({
  name: input.title,
  description: input.description,
  period: input.period,
  rule_ids: input.ruleIds.map(mapRuleIdForPayload),
});

type RawDataSource = {
  datasource_id?: unknown;
  datasourceId?: unknown;
  name?: unknown;
  type?: unknown;
  is_active?: unknown;
  isActive?: unknown;
};

const normalizeAllocationDataSource = (source: RawDataSource): AllocationDataSourceSummary => ({
  datasourceId: String(source.datasource_id ?? source.datasourceId ?? ''),
  name: String(source.name ?? 'Untitled'),
  type: String(source.type ?? ''),
  isActive: Boolean(source.is_active ?? source.isActive),
});

const isActiveSapDatasphere = (source: AllocationDataSourceSummary) => (
  source.type.toLowerCase() === 'sap_datasphere' && source.isActive
);

export const allocationValidationApi = {
  async getSapConnectionStatus(): Promise<AllocationSapConnectionStatus> {
    const response = await api.get<RawDataSource[]>('/api/v1/datasources');
    const dataSources = Array.isArray(response.data)
      ? response.data.map(normalizeAllocationDataSource)
      : [];
    const datasource = dataSources.find(isActiveSapDatasphere) ?? null;

    return {
      connected: Boolean(datasource),
      datasource,
    };
  },

  async listRuns(
    query: AllocationValidationRunsQuery = {},
  ): Promise<AllocationValidationRunsResult> {
    const { data } = await api.get<unknown>(`${BASE}/runs`, {
      params: buildRunsQueryParams(query),
    });
    const source = isRecord(data) ? data : {};
    const rawRuns = extractRunsArray(data).map(mapRun);
    const serverLimit = readNumber(source, ['limit', 'page_size', 'pageSize']);
    const serverOffset = readNumber(source, ['offset']);
    const serverNextOffset = readNumber(source, ['next_offset', 'nextOffset']);
    const serverHasMore = readBoolean(source, ['has_more', 'hasMore']);
    const hasServerPagination = (
      serverLimit !== null ||
      serverOffset !== null ||
      serverNextOffset !== null ||
      serverHasMore !== null
    );
    const limit = serverLimit ?? query.limit ?? rawRuns.length;
    const offset = serverOffset ?? query.offset ?? 0;
    const filteredRuns = rawRuns.filter((run) => (
      matchesRunSearch(run, query.search) &&
      matchesRunStatusFilter(run, query.status)
    ));
    const runs = hasServerPagination
      ? filteredRuns
      : filteredRuns.slice(offset, offset + limit);
    const total = hasServerPagination
      ? extractTotal(data, rawRuns.length)
      : filteredRuns.length;
    const hasMore = serverHasMore
      ?? (serverNextOffset !== null ? true : offset + runs.length < total);

    return {
      runs,
      total,
      limit,
      offset,
      hasMore,
      nextOffset: hasMore ? (serverNextOffset ?? offset + limit) : null,
    };
  },

  async listRules(): Promise<AllocationValidationRule[]> {
    const allRules: AllocationValidationRule[] = [];
    let page = await fetchRulesPage({ limit: 100, offset: 0 });
    allRules.push(...page.rules);

    while (page.hasMore && page.nextOffset !== null) {
      page = await fetchRulesPage({ limit: 100, offset: page.nextOffset });
      allRules.push(...page.rules);
    }

    return allRules;
  },

  async listRulesPage(
    query: AllocationValidationRulesQuery = {},
  ): Promise<AllocationValidationRulesResult> {
    return fetchRulesPage(query);
  },

  async createRun(input: AllocationValidationCreateRunInput): Promise<AllocationValidationRunDetail> {
    const { data } = await api.post<unknown>(`${BASE}/runs`, mapCreateRunPayload(input));
    return mapRunDetail(unwrapData(data));
  },

  async getRunDetails(runId: string): Promise<AllocationValidationRunDetail> {
    const { data } = await api.get<unknown>(`${BASE}/runs/${encodeURIComponent(runId)}`);
    return mapRunDetail(unwrapData(data));
  },

  async downloadRuleResult(blobUrl: string, fileName?: string): Promise<void> {
    const response = await api.get<Blob>(blobUrl, {
      responseType: 'blob',
    });
    const objectUrl = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName || '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1_000);
  },
};
